import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SortingQuestionAnswerCard } from '@lessonup/pin-renderer';
import { SortingQuestionPinAnswer, SortingQuestionTeacherPin } from '@lessonup/pins-shared';
import { Button, color, IconDragIndicator, IconRemove, styled } from '@lessonup/ui-components';
import React, { useRef } from 'react';
import { useEditorTranslation } from '../../../../../hooks/useEditorTranslation';
import { TipTapInput } from '../../../../../utils/yjs/TipTapInput';

export interface SortableAnswerCardProps {
  answerId: string;
  index: number;
  answer: SortingQuestionPinAnswer;
  pin: SortingQuestionTeacherPin;
  isPinComponentActive?: boolean;
  onRemove(): void;
  isInteractive?: boolean;
}

export const SortableAnswerCard: React.FC<SortableAnswerCardProps> = ({
  answerId,
  index,
  answer,
  pin,
  isPinComponentActive,
  onRemove,
  isInteractive,
}) => {
  const { t } = useEditorTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isSorting } = useSortable({ id: answerId });
  const inputWrapperRef = useRef<HTMLDivElement>(null);

  // We prevent animations that scale the element, as it can cause visual glitches inside the sortable area.
  const transformNoScale = transform ? { ...transform, scaleX: 1, scaleY: 1 } : null;

  const style = {
    transform: CSS.Transform.toString(transformNoScale),
    // Only animate while dragging, not on deletion
    transition: isSorting ? transition || undefined : undefined,
  };

  const scrollToLeft = () => {
    // Scroll to the left when the input loses focus to ensure the beginning of
    // the line becomes visible.
    const paragraph = inputWrapperRef.current?.querySelector('.ProseMirror p');
    if (paragraph) {
      paragraph.scrollTo({ left: 0 });
    }
  };

  return (
    <div ref={setNodeRef} style={style}>
      <SortingQuestionAnswerCard index={index} answer={answer}>
        <AnswerInputWrapper ref={inputWrapperRef}>
          <TipTapInput
            key="selected"
            pinId={pin.id}
            pinComponentId="sortingAnswer"
            fragmentKey={answerId}
            placeholder={t('answerAreaPinComponent.sortingQuestion.answerPlaceholder', {
              answerIndex: index + 1,
            })}
            onBlur={scrollToLeft}
            disableNewLine
            maxInputLength={400} // This max length should comfortable match the line-cap limit of the student view answer card.
          />
        </AnswerInputWrapper>
        {isInteractive && (
          <>
            {isPinComponentActive && (
              <Button
                buttonType="negative"
                size="medium"
                onClick={onRemove}
                iconStart={<IconRemove />}
                aria-label={t('answerAreaPinComponent.sortingQuestion.removeAnswer', {
                  answerIndex: index + 1,
                })}
              />
            )}
            <Button
              {...attributes}
              {...listeners}
              buttonType="neutral"
              size="medium"
              aria-label={t('answerAreaPinComponent.sortingQuestion.dragAnswer')}
              iconStart={<IconDragIndicator />}
              style={{
                cursor: 'grab',
              }}
            />
          </>
        )}
      </SortingQuestionAnswerCard>
    </div>
  );
};

const AnswerInputWrapper = styled.div`
  &:hover:not(:focus-within) {
    outline: 1px solid ${color.neutral.outline.background};
  }

  &:focus-within {
    outline: 2px solid ${color.utils.focus.stroke};
  }
`;
